/**
 * @voltxampere/engine — Unified stamp dispatch registry
 *
 * Sprint 6 maintained two parallel dispatch maps (DC + transient)
 * each keyed by component type. Sprint 7 collapses both into a
 * single `Map<type, StampDefinition>` where every definition carries
 * `applyDC` (mandatory) and optional `applyTransient`. The transient
 * lookup falls back to `applyDC` when `applyTransient` is absent —
 * correct for linear time-invariant elements (resistor, ground,
 * sources) and for nonlinear devices whose companion model is
 * already re-linearised every Newton iteration (diode, BJT, MOSFET).
 *
 * Public API preserved:
 *
 *  - `registerDCStamps()` / `registerTransientStamps()` both populate
 *    the same underlying map.  Keeping the two-call pattern means the
 *    Sprint 5/6 tests keep working and "only run DC" consumers do not
 *    accidentally get transient lookups they did not expect.
 *  - `getDCStamp(type)` returns `def.applyDC`.
 *  - `getTransientStamp(type)` returns `def.applyTransient ?? def.applyDC`
 *    (the DC stamp is callable with a transient context because
 *    `TransientStampContext extends DCStampContext`).
 *  - `getStampAugmentation(type)` reads from the single unified map —
 *    the Sprint 6.1 re-review smell (DC/transient `augments` drift
 *    risk) is closed because there is now one source of truth.
 *
 * Sprint 7 new stamps (BJT, MOSFET, OpAmp) register themselves
 * alongside the Sprint 5/6 set through {@link registerDCStamps}.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { ContractViolationError } from "@voltxampere/shared";
import { resistorDCStampDefinition, voltageSourceDCStampDefinition, currentSourceDCStampDefinition, groundDCStampDefinition, capacitorDCStampDefinition, inductorDCStampDefinition, switchDCStampDefinition, pulseSourceDCStampDefinition, pwlSourceDCStampDefinition, potentiometerDCStampDefinition, wireNodeDCStampDefinition, bSourceDCStampDefinition, } from "./linear/index.js";
import { diodeDCStampDefinition, ledDCStampDefinition, zenerDCStampDefinition, jfetNDCStampDefinition, jfetPDCStampDefinition, bjtNpnDCStampDefinition, bjtPnpDCStampDefinition, mosfetNmosDCStampDefinition, mosfetPmosDCStampDefinition, opampDCStampDefinition, triacDCStampDefinition, optocouplerTriacDCStampDefinition, } from "./nonlinear/index.js";
// Sprint 41 — Digital logic gate DC stamp (ADR-0061). Combinational
// truth-table evaluation + D-FLOP placeholder; AC + transient
// deferred to Sprint 42+ / 44+ per the ADR's MVP scope.
import { digitalGateDCStampDefinition } from "./digital-gate.js";
// Sprint 43 — Analog multiplexer DC stamp (ADR-0063). Two canonical
// types (`analog-mux-8`, `analog-mux-16`) share one stamp implementation
// pinned to channelCount + selectPinCount at registration time. Both
// stamps follow the Sprint 4 voltage-source augmented row pattern;
// AC + transient deferred to Sprint 50+ smooth-threshold work.
import { analogMux8DCStampDefinition, analogMux16DCStampDefinition } from "./analog-mux.js";
// Sprint 46 — MCU execution canonical engine type (ADR-0067). Engine
// extension #4. Sprint 46 MVP: stamp registers as no-op so 22 mevcut
// sentinel bit-identical kalır. Sprint 47-2 emulator entegrasyonu
// per-pin augmented voltage source pattern reuse.
import { mcuExecutionDCStampDefinition } from "./mcu-execution.js";
// Sprint 48 (ADR-0069) — I2C bus canonical stamp. Engine extension #5
// MVP: stamp body is a topological no-op so the 25 mevcut frozen
// sentinel'ler (Sprint 47 baseline) bit-identical kalır. Bus FSM helper
// (`I2cBusFsm` in i2c-bus-fsm.ts) is consulted by the test runner /
// Sprint 48-7 sensor activation layer between solves.
import { i2cBusDCStampDefinition } from "./i2c-bus.js";
// Sprint 48 (ADR-0069) — SPI bus canonical stamp. Engine extension #5
// second half. Same no-op DC body + 8-bit shift register FSM helper
// (`SpiBusFsm` in spi-bus-fsm.ts).
import { spiBusDCStampDefinition } from "./spi-bus.js";
import { capacitorTransientStampDefinition, inductorTransientStampDefinition, resistorTransientStampDefinition, voltageSourceTransientStampDefinition, currentSourceTransientStampDefinition, groundTransientStampDefinition, diodeTransientStampDefinition, ledTransientStampDefinition, zenerTransientStampDefinition, jfetNTransientStampDefinition, jfetPTransientStampDefinition, bjtTransientStampDefinition, bjtPnpTransientStampDefinition, mosfetTransientStampDefinition, mosfetPmosTransientStampDefinition, opampTransientStampDefinition, } from "./transient/index.js";
import { resistorACStamp, capacitorACStamp, inductorACStamp, voltageSourceACStamp, currentSourceACStamp, groundACStamp, acSourceACStamp, diodeACStamp, ledACStamp, zenerACStamp, jfetNACStamp, jfetPACStamp, potentiometerACStamp, wireNodeACStamp, bjtACStamp, mosfetACStamp, opampACStamp, switchACStamp, pulseSourceACStamp, pwlSourceACStamp, } from "./ac/index.js";
const dispatch = new Map();
let dcRegistered = false;
let transientRegistered = false;
let acRegistered = false;
/**
 * Sprint 14.0.1 / Sprint 16 follow-up — schema component-type
 * aliases.
 *
 * Sprint 10.1's symbol library and Sprint 16's SPICE codecs both
 * emit components keyed by short, symbol-id-style type strings
 * (`dc-source`, `npn`, `nmos`, `led`, …). The engine stamps,
 * however, register under the canonical component-library names
 * (`voltage-source`, `bjt-npn`, `mosfet-nmos`, `diode`, …).
 *
 * The mismatch surfaced when Sprint 14.0.1 wired the engine
 * worker to F5: every canvas-demo run threw
 * `planCircuit: no stamp definition for dc-source`. The fix is
 * pure registration — we map each symbol-id type onto the same
 * `StampDefinition` already registered for its canonical
 * counterpart, so a single `applyDC` / `applyTransient` pair
 * answers to both names. The engine algorithm is untouched and
 * the frozen-regression sentinels (V_mid=5, V_anode=0.6929,
 * V_BE=0.7917, PNP CE 8 iter, V_C(1ms)=6.3212) keep using their
 * canonical types verbatim.
 *
 * Symbol-ids without a backing canonical stamp (`ac-source`,
 * `pulse-source` — different param shapes; `jfet`, `switch`,
 * `potentiometer` — no stamp at all) are intentionally absent
 * from the alias table. They surface the existing "no stamp
 * registered" error so a future sprint can add proper stamps
 * (Sprint 19+ for AC/Pulse DC OP, Sprint 22+ for jfet/switch).
 */
const SCHEMA_SYMBOL_ALIASES = {
    // Sprint 10.1 dc-source symbol → Sprint 5 voltage-source stamp.
    // Both use `params.voltage`, both connect via `pos`/`neg`
    // terminals — direct compatibility, no adapter needed.
    "dc-source": "voltage-source",
    // BJT — Sprint 10.1 split symbols (`npn`, `pnp`) ↔ Sprint 7
    // canonical (`bjt-npn`, `bjt-pnp`).
    npn: "bjt-npn",
    pnp: "bjt-pnp",
    // MOSFET — same split-symbol pattern.
    nmos: "mosfet-nmos",
    pmos: "mosfet-pmos",
    // Op-amp — Sprint 10.1 dashed id ↔ Sprint 7 canonical.
    "op-amp": "opamp",
    // Sprint 23 B1: LED no longer aliases diode — it has its own
    // dedicated stamp (LED-default Is=1e-12, n=3).
    // Sprint 23 B2: Zener no longer aliases diode — dedicated stamp
    // with forward Shockley + reverse breakdown branches.
    // Sprint 20 — `ac-source` is now a dedicated component definition
    // (D-20-3 option C: additive). The string entry above used to be
    // a phantom; the alias map no longer needs to redirect it because
    // there is a real `ac-source` stamp registered. Listing it here as
    // a NO-OP (alias to itself) is a defensive placeholder so a future
    // sprint that introduces an `ac-source` SHORTHAND symbol id does
    // not accidentally collide.
};
/**
 * All Sprint 5 DC stamp envelopes in registration order. Each
 * definition carries `applyDC` and (for capacitor/inductor and
 * friends) *no* `applyTransient` — the transient companions land
 * in the Sprint 6 list below and are merged on top.
 */
const SPRINT_5_DEFINITIONS = [
    resistorDCStampDefinition,
    voltageSourceDCStampDefinition,
    currentSourceDCStampDefinition,
    groundDCStampDefinition,
    capacitorDCStampDefinition,
    inductorDCStampDefinition,
    diodeDCStampDefinition,
    // Sprint 23 B1: LED dedicated stamp (Is=1e-12, n=3 LED defaults
    // injected before delegating to the diode body). Replaces the
    // Sprint 14.0.1 SCHEMA_SYMBOL_ALIASES.led = "diode" mapping.
    ledDCStampDefinition,
    // Sprint 23 B2: Zener dedicated stamp (forward Shockley + reverse
    // breakdown branch in a single smooth Newton-friendly equation).
    // Replaces the Sprint 14.0.1 SCHEMA_SYMBOL_ALIASES.zener = "diode"
    // mapping.
    zenerDCStampDefinition,
    // Sprint 23 B3: JFET dedicated stamps (Shichman-Hodges square-law,
    // n-channel default Vt0=-2V depletion-mode, p-channel +2V).
    jfetNDCStampDefinition,
    jfetPDCStampDefinition,
    // Sprint 7: real BJT Ebers-Moll + MOSFET Shichman-Hodges stamps.
    // OpAmp stays on the placeholder pending its Sprint 7 commit.
    bjtNpnDCStampDefinition,
    bjtPnpDCStampDefinition,
    mosfetNmosDCStampDefinition,
    mosfetPmosDCStampDefinition,
    opampDCStampDefinition,
    // Sprint 22 B1 — controlled switch (linear conductance, no
    // augmentation, no transient state).
    switchDCStampDefinition,
    // Sprint 22 B2 — PULSE voltage source. Independent V source whose
    // value follows the SPICE PULSE(V1, V2, TD, TR, TF, PW, PER)
    // waveform; the same DC stamp body is reused for transient via
    // the StampDefinition fall-through (the value is recomputed each
    // call from `ctx.t`).
    pulseSourceDCStampDefinition,
    // Sprint 22 B3 — PWL voltage source. Linear interpolation between
    // user-supplied JSON-encoded breakpoints; same independent-source
    // augmented row as PULSE.
    pwlSourceDCStampDefinition,
    // Sprint 23 B4 — Potentiometer 3-pin (top/wiper/bottom). Pure
    // conductance, no augmentation, no transient state.
    potentiometerDCStampDefinition,
    // Sprint 23 B5 — Wire-Node 4-pin no-op (electrical 0Ω junction;
    // the net layer carries the equipotential constraint).
    wireNodeDCStampDefinition,
    // Sprint 41 — Digital logic gate. One stamp covers all
    // combinational gates via the `gateFunction` param. D-FLOP is a
    // placeholder per ADR-0061 §"MVP DC-only scope" — full sequential
    // simulation lands in Sprint 42+.
    digitalGateDCStampDefinition,
    // Sprint 43 (ADR-0063) — Analog multiplexer family.
    // `analog-mux-8` covers 4051 / 4052 / 74HC4051; `analog-mux-16`
    // covers 4067. Same Sprint 4 voltage-source augmented row pattern,
    // dimensions baked at registration time.
    analogMux8DCStampDefinition,
    analogMux16DCStampDefinition,
    // Sprint 46 (ADR-0067) — MCU execution canonical engine type.
    // Engine extension #4. Sprint 46 MVP: stamp is a no-op so the 22
    // mevcut frozen sentinels stay bit-identical (no MCU in any of
    // them). Sprint 47-2 wires the AVR emulator into per-pin GPIO
    // output drives via the Sprint 4 voltage-source augmented row
    // pattern; sentinel deltas land in Sprint 47-3.
    mcuExecutionDCStampDefinition,
    // Sprint 48 (ADR-0069) — I2C bus canonical engine type. Engine
    // extension #5 MVP: DC stamp is no-op (bus is topologically passive
    // — slaves + master drive their own pin voltages). The 5-phase FSM
    // helper (`I2cBusFsm`) lives in a separate module that the test
    // runner / sensor activation layer consults between solves.
    // Sprint 49+ may add Norton stamps for pull-up resistors.
    i2cBusDCStampDefinition,
    // Sprint 48 (ADR-0069) — SPI bus canonical engine type. Engine
    // extension #5 second half. Same MVP shape as i2c-bus: DC stamp is
    // no-op, 8-bit MOSI/MISO shift register FSM (`SpiBusFsm`) is
    // consulted between solves. Mode 0 only in Sprint 48 MVP; mode
    // 1/2/3 → Sprint 50+.
    spiBusDCStampDefinition,
    // Sprint 54.2b-1 (ADR-0087) — TRIAC bidirectional latch. State
    // machine via augmented internal-node row (1 per instance, always).
    // DC stamp pins state to 0 (off); transient stamp derives state
    // from xStepPrev + V_gate + V_diff_prev. ngspice BT136 parity.
    triacDCStampDefinition,
    // Sprint 54.2b-2 (ADR-0087) — MOC zero-crossing optocoupler. LED
    // side + photo-TRIAC pair with augmented-row state plumbing (TRIAC
    // pattern reuse). Two isolated networks; Sprint 8b reduced-KCL
    // convergence handles them naturally.
    optocouplerTriacDCStampDefinition,
    // Sprint 54.2b-5 (ADR-0087) — B-source expression-driven voltage/
    // current source. Per-iter DSL evaluation against current iterate's
    // V(net) / I(component) values. Dynamic augments: 'vsource' for
    // voltage mode, [] for current mode. AST cached per instance.
    bSourceDCStampDefinition,
];
/**
 * Sprint 6 transient stamp envelopes. Each definition contributes an
 * `applyTransient` (via the legacy `defineTransientStamp` shim) that
 * is merged onto the existing map entry.  For time-invariant
 * passthrough types (resistor / V source / …) merging is a no-op —
 * the transient fn equals the DC fn and `applyDC` is already set.
 */
const SPRINT_6_TRANSIENT_DEFINITIONS = [
    resistorTransientStampDefinition,
    voltageSourceTransientStampDefinition,
    currentSourceTransientStampDefinition,
    groundTransientStampDefinition,
    capacitorTransientStampDefinition,
    inductorTransientStampDefinition,
    diodeTransientStampDefinition,
    // Sprint 23 B1 — LED transient stamp (delegates to LED DC stamp).
    ledTransientStampDefinition,
    // Sprint 23 B2 — Zener transient stamp (delegates to Zener DC stamp).
    zenerTransientStampDefinition,
    // Sprint 23 B3 — JFET transient stamps (delegates to JFET DC stamps).
    jfetNTransientStampDefinition,
    jfetPTransientStampDefinition,
    bjtTransientStampDefinition,
    bjtPnpTransientStampDefinition,
    mosfetTransientStampDefinition,
    mosfetPmosTransientStampDefinition,
    opampTransientStampDefinition,
];
const SPRINT_20_AC_DEFINITIONS = [
    { type: "resistor", applyAC: resistorACStamp },
    { type: "capacitor", applyAC: capacitorACStamp },
    { type: "inductor", applyAC: inductorACStamp },
    { type: "voltage-source", applyAC: voltageSourceACStamp },
    { type: "current-source", applyAC: currentSourceACStamp },
    { type: "ground", applyAC: groundACStamp },
    { type: "diode", applyAC: diodeACStamp },
    // Sprint 23 B1 — LED dedicated AC stamp (diode wrapper).
    { type: "led", applyAC: ledACStamp },
    // Sprint 23 B2 — Zener dedicated AC stamp (forward + breakdown
    // small-signal sum).
    { type: "zener", applyAC: zenerACStamp },
    // Sprint 23 B3 — JFET AC stamps (gm/gds + optional gate caps).
    { type: "jfet-n", applyAC: jfetNACStamp },
    { type: "jfet-p", applyAC: jfetPACStamp },
    // Sprint 23 B4 — Potentiometer AC stamp (pure conductance, real).
    { type: "potentiometer", applyAC: potentiometerACStamp },
    // Sprint 23 B5 — Wire-Node AC no-op (frequency-independent 0Ω).
    { type: "wire-node", applyAC: wireNodeACStamp },
    { type: "bjt-npn", applyAC: bjtACStamp },
    { type: "bjt-pnp", applyAC: bjtACStamp },
    { type: "mosfet-nmos", applyAC: mosfetACStamp },
    { type: "mosfet-pmos", applyAC: mosfetACStamp },
    { type: "opamp", applyAC: opampACStamp },
    // Sprint 22 B1 — switch is frequency-independent.
    { type: "switch", applyAC: switchACStamp },
    // Sprint 22 B2 — PULSE source has zero AC small-signal content.
    { type: "pulse-source", applyAC: pulseSourceACStamp },
    // Sprint 22 B3 — PWL source likewise zero AC small-signal.
    { type: "pwl-source", applyAC: pwlSourceACStamp },
];
/**
 * ac-source value at simulation time `t`. Sprint 20 ADR-0039 ships
 * the type with applyDC = dcOffset only (zero-time evaluation) and
 * applyAC = phasor (small-signal sweep). Sprint 54.3d-2 extends the
 * time-domain side: during transient analysis the same stamp must
 * emit a sinusoid `dcOffset + amplitude · sin(2π·freq·t + phase)`
 * so the engine matches ngspice's `SIN(offset amp freq)` syntax
 * bit-for-bit.
 *
 * At ctx.t = 0 (DC OP) the expression collapses to
 * `dcOffset + amplitude · sin(phase_rad)`. For phase=0 this is just
 * `dcOffset` (preserves the Sprint 20 behavior bit-identical when
 * phase defaults). For non-zero phase the DC OP shifts by
 * `amplitude · sin(phase_rad)` — analytically correct for an AC
 * source whose phase is non-zero at the moment of analysis.
 *
 * Engine extension #6 (ADR-0095 §54.3d). Stamp baseline reset to
 * `sprint-54-3d-baseline` after this lands.
 */
function acSourceValueAt(component, t) {
    const params = component.params;
    const dcOffsetRaw = params.dcOffset;
    const dcOffset = typeof dcOffsetRaw === "number" && Number.isFinite(dcOffsetRaw) ? dcOffsetRaw : 0;
    const amplitudeRaw = params.amplitude;
    const amplitude = typeof amplitudeRaw === "number" && Number.isFinite(amplitudeRaw) ? amplitudeRaw : 0;
    const freqRaw = params.freq;
    const freq = typeof freqRaw === "number" && Number.isFinite(freqRaw) && freqRaw > 0 ? freqRaw : 1;
    const phaseDegRaw = params.phase;
    const phaseDeg = typeof phaseDegRaw === "number" && Number.isFinite(phaseDegRaw) ? phaseDegRaw : 0;
    const phaseRad = (phaseDeg * Math.PI) / 180;
    return dcOffset + amplitude * Math.sin(2 * Math.PI * freq * t + phaseRad);
}
const acSourceDCStamp = (ctx, component) => {
    const v = acSourceValueAt(component, ctx.t);
    const posTerm = component.terminals.find((t) => t.terminalId === "pos");
    const negTerm = component.terminals.find((t) => t.terminalId === "neg");
    if (posTerm === undefined || negTerm === undefined) {
        throw new ContractViolationError(`acSourceDCStamp: component "${component.id}" missing pos/neg terminal`);
    }
    const pos = ctx.nodeIndex(posTerm.netId);
    const neg = ctx.nodeIndex(negTerm.netId);
    const k = ctx.vSourceIndex(component.id);
    ctx.addY(pos, k, 1);
    ctx.addY(neg, k, -1);
    ctx.addY(k, pos, 1);
    ctx.addY(k, neg, -1);
    ctx.rhs[k] = (ctx.rhs[k] ?? 0) + v * ctx.sourceScale;
};
const acSourceDefinition = {
    type: "ac-source",
    augments: ["vsource"],
    applyDC: acSourceDCStamp,
    // Sprint 54.3d-2 — applyTransient reuses applyDC because the
    // value formula already evaluates at ctx.t. The unified-stamp
    // contract permits the fallback (StampDefinition §applyTransient
    // optional). pulse-source uses the same pattern.
    applyAC: acSourceACStamp,
};
/**
 * Merge a transient stamp definition onto whatever the DC pass
 * already put in the map. The DC `applyDC` is preserved — the
 * transient side only ever contributes `applyTransient` and keeps
 * `augments` in sync (the two sides must agree on augments; the
 * unified {@link StampDefinition} removes the drift risk going
 * forward).
 */
function mergeTransientDefinition(def) {
    const existing = dispatch.get(def.type);
    if (existing === undefined) {
        dispatch.set(def.type, def);
        return;
    }
    if (!arraysEqual(existing.augments, def.augments)) {
        throw new ContractViolationError(`registerTransientStamps: augments mismatch for type "${def.type}" (DC registry says ${JSON.stringify(existing.augments)}, transient definition says ${JSON.stringify(def.augments)})`);
    }
    dispatch.set(def.type, {
        ...existing,
        applyTransient: def.applyTransient ?? def.applyDC,
    });
}
function arraysEqual(a, b) {
    if (a.length !== b.length)
        return false;
    for (let i = 0; i < a.length; i++) {
        if (a[i] !== b[i])
            return false;
    }
    return true;
}
/**
 * Register schema-symbol-id aliases for already-registered
 * canonical stamps. Each alias points to the SAME
 * `StampDefinition` instance as its canonical counterpart, so
 * `dispatch.get("dc-source") === dispatch.get("voltage-source")`
 * after registration. The alias's `type` field reads as the
 * canonical name (the StampDefinition is shared by reference);
 * lookups don't read the field, so this is invisible to callers.
 *
 * Idempotent (always overwrites). Re-binding is required after
 * `registerTransientStamps` because `mergeTransientDefinition`
 * replaces the canonical entry with a new object that carries
 * `applyTransient` — alias entries captured before that point
 * would otherwise be stale.
 *
 * Aliases for canonical types that have not yet been registered
 * are skipped silently.
 */
function registerSchemaSymbolAliases() {
    for (const [alias, canonical] of Object.entries(SCHEMA_SYMBOL_ALIASES)) {
        const target = dispatch.get(canonical);
        if (target === undefined)
            continue;
        dispatch.set(alias, target);
    }
}
/**
 * Register the Sprint 5 stamp set. Idempotent: subsequent calls
 * after the first are no-ops, so consumers can call it during
 * test setup without worrying about double-registration.
 */
export function registerDCStamps() {
    if (dcRegistered)
        return;
    for (const def of SPRINT_5_DEFINITIONS) {
        const existing = dispatch.get(def.type);
        if (existing === undefined) {
            dispatch.set(def.type, def);
        }
        else {
            // Transient was registered first — keep its applyTransient and
            // overlay this DC pass's applyDC (usually the same function).
            //
            // Sprint 7.1 P1-2: mirror the augments-consistency check that
            // `mergeTransientDefinition` does in the opposite direction.
            // Without this, a future stamp whose DC definition declares
            // different augments than its transient counterpart would land
            // silently when transient registered first — an asymmetric
            // version of the drift bug Sprint 7's unified StampDefinition
            // was supposed to kill. Both registration orders now enforce
            // the invariant.
            if (!arraysEqual(existing.augments, def.augments)) {
                throw new ContractViolationError(`registerDCStamps: augments mismatch for type "${def.type}" (transient registry says ${JSON.stringify(existing.augments)}, DC definition says ${JSON.stringify(def.augments)})`);
            }
            dispatch.set(def.type, { ...existing, applyDC: def.applyDC });
        }
    }
    // Sprint 54.3d-2 — ac-source is no longer AC-sweep-only. Its
    // applyDC now evaluates a time-varying sinusoid via `ctx.t`,
    // making it a first-class time-domain source. Register it here
    // so DC + transient solvers see the type without `registerACStamps()`
    // being a prerequisite. `registerACStamps()` still overlays the
    // AC phasor stamp on top of the same definition entry — it
    // becomes idempotent for the DC side.
    const existingAC = dispatch.get(acSourceDefinition.type);
    if (existingAC === undefined) {
        dispatch.set(acSourceDefinition.type, acSourceDefinition);
    }
    else {
        if (!arraysEqual(existingAC.augments, acSourceDefinition.augments)) {
            throw new ContractViolationError(`registerDCStamps: augments mismatch for type "${acSourceDefinition.type}"`);
        }
        dispatch.set(acSourceDefinition.type, {
            ...existingAC,
            applyDC: acSourceDefinition.applyDC,
        });
    }
    // Sprint 14.0.1 / Sprint 16 follow-up: bind the schema-symbol-id
    // aliases (dc-source → voltage-source, npn → bjt-npn, …) onto
    // the freshly populated canonical entries.
    registerSchemaSymbolAliases();
    dcRegistered = true;
}
/**
 * Look up the DC stamp for a registered component type. Returns
 * `undefined` if no definition is registered for the type.
 */
export function getDCStamp(type) {
    return dispatch.get(type)?.applyDC;
}
/**
 * Look up the full stamp definition (DC + optional transient apply,
 * augmentation metadata) for a registered component type. Used by
 * the planner to size the MNA system. Returns `undefined` if the
 * type has no registration.
 */
export function getDCStampDefinition(type) {
    return dispatch.get(type);
}
/**
 * Convenience reader for the planner. Returns the registered
 * augmentation kinds for the type, or `undefined` if the type is
 * not in the registry. Treats `undefined` as a hard error — the
 * caller must have forgotten to register stamps.
 */
export function getStampAugmentation(type) {
    return dispatch.get(type)?.augments;
}
/**
 * Strict variant: throws {@link ContractViolationError} if no stamp
 * is registered for the type. The MNA builder uses this so a missing
 * stamp surfaces loudly rather than silently producing a wrong
 * matrix.
 */
export function getDCStampOrThrow(type) {
    const def = dispatch.get(type);
    if (def === undefined) {
        throw new ContractViolationError(`getDCStampOrThrow: no DC stamp registered for type "${type}" — did you forget to call registerDCStamps()?`);
    }
    return def.applyDC;
}
/** True once `registerDCStamps()` has run successfully. */
export function areDCStampsRegistered() {
    return dcRegistered;
}
/**
 * Testing hook — **do not call in production code**. Clears the
 * dispatch map and ALL phase flags (DC + transient + AC) so vitest
 * cases can exercise the "stamp missing" path. Sprint 20 added the
 * AC flag to the reset; previously a subsequent test would observe
 * `acRegistered = true` against an empty dispatch map.
 */
export function __resetDCStampsForTests() {
    dispatch.clear();
    dcRegistered = false;
    transientRegistered = false;
    acRegistered = false;
}
/**
 * Register the Sprint 6 transient stamp set. Idempotent — safe to
 * call multiple times. Must be called after {@link registerDCStamps}
 * so the augment-metadata invariant can be checked.
 */
export function registerTransientStamps() {
    if (transientRegistered)
        return;
    for (const def of SPRINT_6_TRANSIENT_DEFINITIONS) {
        mergeTransientDefinition(def);
    }
    // Re-bind aliases — registerTransientStamps may have replaced
    // the underlying StampDefinition entries (added applyTransient),
    // so any alias mapping captured before this call would now
    // point at a stale object. Re-run is cheap (idempotent) and
    // guarantees `getTransientStamp("dc-source")` works.
    registerSchemaSymbolAliases();
    transientRegistered = true;
}
/**
 * Look up the transient stamp for a registered component type.
 * Falls back to the DC apply function when no transient-specific
 * apply is registered. Returns `undefined` if the type has no
 * entry in the registry at all.
 */
export function getTransientStamp(type) {
    const def = dispatch.get(type);
    if (def === undefined)
        return undefined;
    if (def.applyTransient !== undefined)
        return def.applyTransient;
    // DCStampFn is assignable to TransientStampFn under strictFunctionTypes:
    // `TransientStampContext extends DCStampContext`, so a function
    // that accepts the narrower DC context accepts any transient
    // context too (the extra fields are simply ignored).
    return def.applyDC;
}
/** Look up the full transient stamp definition envelope. */
export function getTransientStampDefinition(type) {
    return dispatch.get(type);
}
/**
 * Strict variant: throws {@link ContractViolationError} when no
 * transient stamp is registered for the type. The transient MNA
 * builder uses this so a missing stamp surfaces loudly rather than
 * silently producing a wrong matrix.
 */
export function getTransientStampOrThrow(type) {
    const stamp = getTransientStamp(type);
    if (stamp === undefined) {
        throw new ContractViolationError(`getTransientStampOrThrow: no transient stamp registered for type "${type}" — did you forget to call registerTransientStamps()?`);
    }
    return stamp;
}
/** True once {@link registerTransientStamps} has run successfully. */
export function areTransientStampsRegistered() {
    return transientRegistered;
}
/**
 * Testing hook — alias for {@link __resetDCStampsForTests}. Both DC
 * and transient flags reset together because the unified dispatch
 * map backs both sides.
 */
export function __resetTransientStampsForTests() {
    __resetDCStampsForTests();
}
// ──────────────────────────────────────────────────────────────────
// Sprint 20 AC stamp registration
// ──────────────────────────────────────────────────────────────────
function mergeACOverlay(type, applyAC) {
    const existing = dispatch.get(type);
    if (existing === undefined) {
        throw new ContractViolationError(`mergeACOverlay: cannot overlay AC stamp on unknown type "${type}" (call registerDCStamps first)`);
    }
    dispatch.set(type, { ...existing, applyAC });
}
/**
 * Register the Sprint 20 AC stamp set. Idempotent — second and
 * subsequent calls are no-ops. MUST be called after
 * {@link registerDCStamps} (and ideally
 * {@link registerTransientStamps}) so the overlay has an existing
 * envelope to extend.
 */
export function registerACStamps() {
    if (acRegistered)
        return;
    if (!dcRegistered) {
        throw new ContractViolationError("registerACStamps: registerDCStamps() must run first (AC overlays DC envelopes)");
    }
    for (const { type, applyAC } of SPRINT_20_AC_DEFINITIONS) {
        mergeACOverlay(type, applyAC);
    }
    // ac-source is the only canonical type that gets registered
    // *for the first time* via the AC pass. Insert (or overwrite)
    // the dedicated definition wholesale.
    dispatch.set(acSourceDefinition.type, acSourceDefinition);
    // Re-bind aliases since the underlying definitions were replaced.
    registerSchemaSymbolAliases();
    acRegistered = true;
}
/**
 * Look up the AC stamp for a registered component type. Returns
 * `undefined` if the type has no AC overlay (e.g. types registered
 * pre-Sprint-20 that we have not extended yet) OR if the type
 * itself is unregistered.
 */
export function getACStamp(type) {
    return dispatch.get(type)?.applyAC;
}
/**
 * Strict variant — throws when no AC stamp is registered for the
 * type. The AC matrix builder uses this so missing AC support
 * surfaces loudly rather than silently producing a wrong matrix.
 */
export function getACStampOrThrow(type) {
    const fn = dispatch.get(type)?.applyAC;
    if (fn === undefined) {
        throw new ContractViolationError(`getACStampOrThrow: no AC stamp registered for type "${type}" — did you forget to call registerACStamps()?`);
    }
    return fn;
}
/** True once {@link registerACStamps} has run successfully. */
export function areACStampsRegistered() {
    return acRegistered;
}
/**
 * Testing hook — alias for {@link __resetDCStampsForTests}. The DC
 * reset already wipes the AC flag now (Sprint 20), so this name is
 * kept only for symmetry with the DC/transient pattern.
 */
export function __resetACStampsForTests() {
    __resetDCStampsForTests();
}
//# sourceMappingURL=register.js.map